import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

/**
 * Quick-edit modal — a compact single-item editor for the most-touched
 * fields. Larger changes (colours, images-per-colour, size charts) still
 * open the full ProductForm / ReadyMadeListingEditor via the "Full Edit"
 * button in the card gear menu.
 *
 * onSave receives a shallow patch payload the caller sends to the
 * corresponding update endpoint. On success the caller merges the patch
 * into local state (no full-page refresh needed).
 */
export default function QuickEditModal({ open, item, mode, onClose, onSave }) {
  const priceField = mode === "ready-made" ? "price" : "base_price";
  const [form, setForm] = useState(() => ({
    name: item?.name || "",
    sku: item?.sku || "",
    price: item?.[priceField] ?? 0,
    sale_price: item?.sale_price ?? "",
    stock_status: item?.stock_status || "in_stock",
    category: item?.category || "",
    tags: (item?.tags || []).join(", "),
    active: item?.active ?? true,
    featured: item?.featured ?? false,
    image: mode === "ready-made" ? (item?.image_front || "") : (item?.image || ""),
    description: item?.description || "",
  }));
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const on = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim(),
        [priceField]: parseFloat(form.price) || 0,
        sale_price: form.sale_price === "" ? null : parseFloat(form.sale_price),
        stock_status: form.stock_status,
        category: form.category.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        active: !!form.active,
        featured: !!form.featured,
        description: form.description,
      };
      if (mode === "ready-made") {
        payload.image_front = form.image.trim();
      } else {
        payload.image = form.image.trim();
      }
      await onSave(payload);
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error("Save failed: " + (e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quick-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="surface-card max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[#222] flex justify-between items-center">
          <div>
            <h3 className="font-display text-3xl uppercase">Quick Edit</h3>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1 truncate">
              {item?.id}
            </div>
          </div>
          <button onClick={onClose} data-testid="quick-edit-close"><X size={18} /></button>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Title">
            <input value={form.name} onChange={on("name")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-name" />
          </Field>
          <Field label="SKU">
            <input value={form.sku} onChange={on("sku")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-sku" />
          </Field>
          <Field label="Price (CAD)">
            <input type="number" step="0.01" value={form.price} onChange={on("price")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-price" />
          </Field>
          <Field label="Sale Price (blank = none)">
            <input type="number" step="0.01" value={form.sale_price} onChange={on("sale_price")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-sale-price" />
          </Field>
          <Field label="Category">
            <input value={form.category} onChange={on("category")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-category" />
          </Field>
          <Field label="Stock Status">
            <select value={form.stock_status} onChange={on("stock_status")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none" data-testid="qe-stock-status">
              <option value="in_stock">In stock</option>
              <option value="low_stock">Low stock</option>
              <option value="out_of_stock">Out of stock</option>
            </select>
          </Field>
          <Field label="Tags (comma-separated)" full>
            <input value={form.tags} onChange={on("tags")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-tags" />
          </Field>
          <Field label="Main Image URL" full>
            <input value={form.image} onChange={on("image")} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none focus:border-[var(--tc-red)]" data-testid="qe-image" />
          </Field>
          <Field label="Description" full>
            <textarea value={form.description} onChange={on("description")} rows={3} className="w-full bg-black border border-[#222] px-3 py-2 text-sm outline-none resize-none focus:border-[var(--tc-red)]" data-testid="qe-description" />
          </Field>
          <label className="flex items-center gap-2 font-mono text-xs">
            <input type="checkbox" checked={form.active} onChange={on("active")} data-testid="qe-active" /> ACTIVE
          </label>
          <label className="flex items-center gap-2 font-mono text-xs">
            <input type="checkbox" checked={form.featured} onChange={on("featured")} data-testid="qe-featured" /> FEATURED
          </label>
        </div>

        <div className="p-6 border-t border-[#222] flex justify-end gap-3">
          <button onClick={onClose} className="btn-tc-secondary">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-tc-primary disabled:opacity-50" data-testid="quick-edit-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-1">{label.toUpperCase()}</label>
      {children}
    </div>
  );
}
